import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Target, Eye, Rocket, Globe2, Award } from "lucide-react";
import { Reveal, Stagger, staggerItem } from "@/components/Motion";
import { motion } from "framer-motion";
import CtaBanner from "@/components/CtaBanner";
import Seo from "@/components/Seo";
import { IMAGES, STATS } from "@/lib/site-data";

export default function About() {
  const principles = [
    { Icon: Target, title: "Mission", body: "Equip ambitious organizations and individuals with the technology capability to lead — through consulting, talent, and training." },
    { Icon: Eye, title: "Vision", body: "A world where every business — regardless of size — can wield enterprise-grade technology with confidence." },
    { Icon: Rocket, title: "Ambition", body: "Be the preferred growth partner for companies modernizing around AI, cloud, and DevOps — at human speed." },
  ];

  return (
    <div data-testid="page-about">
      <Seo
        title="About Cutting Edge Technologies Consulting | Houston, TX IT Consulting Firm"
        description="Learn about Cutting Edge Technologies Consulting — a Houston-headquartered IT consulting firm serving Richmond, Sugar Land, Fulshear TX, Washington DC, and the United States."
        path="/about"
      />
      {/* Hero */}
      <section className="relative bg-slate-950 text-white pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 grid-lines-dark opacity-60" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500/10 blur-3xl rounded-full" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="eyebrow text-emerald-400">About Cutting Edge Technologies</div>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] max-w-5xl">
              A new kind of consulting firm — engineered for the AI era.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl text-lg md:text-xl text-slate-300 leading-relaxed">
              Cutting Edge Technologies Consulting is a Houston-headquartered firm helping
              organizations modernize, scale, and win through four interconnected practices:
              consulting, training, managed services, and technology staffing.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-28 md:py-36 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {principles.map((p, i) => (
              <motion.div key={p.title} variants={staggerItem} className="bg-slate-50 border border-slate-200 p-10">
                <p.Icon className="w-6 h-6 text-emerald-600" strokeWidth={1.5} />
                <div className="eyebrow mt-6 text-slate-500">0{i + 1}</div>
                <h3 className="mt-3 font-display text-3xl font-semibold text-slate-900 tracking-tight">{p.title}</h3>
                <p className="mt-4 text-slate-600 leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Leadership credibility */}
      <section className="py-28 md:py-36 bg-slate-50">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative">
                <div className="absolute inset-0 border border-emerald-500 translate-x-4 translate-y-4" />
                <img src={IMAGES.aboutTeam} alt="Cutting Edge Technologies leadership and team" className="relative w-full h-[560px] object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <div className="eyebrow text-emerald-600">Leadership</div>
              <h2 className="mt-4 font-display text-4xl md:text-5xl font-semibold text-slate-900 tracking-tight leading-[1.05]">
                Built by engineers. Led by operators.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-slate-600 text-lg leading-relaxed">
                Cutting Edge Technologies was founded by practitioners with deep roots in IT, engineering, and business —
                professionals who have architected platforms at global enterprises, founded technology
                companies, and trained the next generation of engineers. That mix is the difference between
                advice that looks good in a slide and work that performs in production.
              </p>
            </Reveal>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-8">
              {STATS.map((s) => (
                <Reveal key={s.label} delay={0.12}>
                  <div>
                    <div className="font-display text-3xl md:text-4xl font-semibold text-slate-900">{s.value}</div>
                    <div className="mt-1 text-xs text-slate-500 uppercase tracking-widest">{s.label}</div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <div className="mt-10 flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-3 text-slate-600">
                  <Globe2 className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                  <span className="text-sm">Delivering across 12+ countries</span>
                </div>
                <div className="flex items-center gap-3 text-slate-600">
                  <Award className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                  <span className="text-sm">AWS / Azure / GCP partner-grade</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values split */}
      <section className="py-28 md:py-36 bg-slate-950 text-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 items-end">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="eyebrow text-emerald-400">Operating principles</div>
                <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
                  How we show up for every engagement.
                </h2>
              </Reveal>
            </div>
          </div>
          <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { title: "Senior by default", body: "Engagements are led by senior engineers and architects — no bait-and-switch." },
              { title: "Outcome-first", body: "We scope to business outcomes, not hours. You know what you're buying." },
              { title: "Transfer knowledge", body: "Every project leaves your team more capable than we found it." },
              { title: "Built in the open", body: "Weekly demos, transparent dashboards, no theatre. Progress is observable." },
            ].map((v) => (
              <motion.div key={v.title} variants={staggerItem} className="border border-slate-800 p-10 hover:border-emerald-500/60 transition-colors">
                <div className="font-display text-2xl font-semibold">{v.title}</div>
                <p className="mt-3 text-slate-400 leading-relaxed">{v.body}</p>
              </motion.div>
            ))}
          </Stagger>
          <Reveal delay={0.2}>
            <div className="mt-14">
              <Link to="/services" data-testid="about-to-services" className="arrow-link text-emerald-400 font-medium">
                See how we apply them across services <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBanner
        title="Partner with a team that takes ownership."
        body="Bring your hardest technology problem. We'll help you define what success looks like — and build it with you."
      />
    </div>
  );
}
